import React, { useEffect, useMemo, useRef, useState } from 'react'
import { inRange } from 'lodash'

import { useSelectionContext } from '../SelectionContext'
import { SelectionAreaRect, SelectionAreaWrapper } from './SelectionArea.styles'
import { StorageItemFragment } from '@sketch/gql-types'

const INITIAL_POSITION = { x: 0, y: 0, shift: false }
const SELECTION_THRESHOLD = 10

type StorageItem = Pick<StorageItemFragment, 'identifier' | '__typename'>

const normalizeElementToStorageItem = (element: HTMLElement): StorageItem => {
  if (element.dataset.shareId) {
    return {
      __typename: 'Share',
      identifier: element.dataset.shareId,
    }
  } else if (element.dataset.projectId) {
    return {
      __typename: 'Project',
      identifier: element.dataset.projectId,
    }
  }

  throw new Error(
    'Element does not have a valid data attribute for storage item'
  )
}

/**
 * SelectionArea
 *
 * This component enables a click-and-drag selection area within its bounds,
 * allowing users to select multiple storage items (shares and projects) by dragging
 * a rectangle over them.
 *
 * How it works:
 *  1. On pointer down within the component, it starts tracking the selection area,
 *     capturing the initial pointer position and collecting all selectable elements.
 *  2. Groups the positions of selectable elements both vertically and horizontally
 *     to optimize selection calculations.
 *  3. As the pointer moves, it updates the selection area end point.
 *  4. It calculates which items fall within the selection area by checking the grouped
 *     positions against the current selection rectangle.
 *  5. On pointer up, it finalizes the selection and resets the selection state.
 *
 * See the inline comments for more detailed explanations of each step.
 */
export const SelectionArea = () => {
  const [isSelecting, setIsSelecting] = useState(false)
  const [startPosition, setStartPosition] = useState(INITIAL_POSITION)
  const [endPosition, setEndPosition] =
    useState<OmitSafe<typeof INITIAL_POSITION, 'shift'>>(INITIAL_POSITION)
  const [selectableElements, setSelectableElements] = useState<HTMLElement[]>(
    []
  )

  const previousSelectionSet = useRef<Set<string>>(new Set())
  const wrapperRef = useRef<HTMLDivElement>(null)

  const { bulkItemSelection, setShowCheckbox } = useSelectionContext()

  useEffect(() => {
    /**
     * 1. Start selection on pointerdown event within the wrapper area
     *
     * This captures the initial pointer position and ensures the selection starts in an area
     * below the clickable items (links, draggable items, etc).
     *
     * We initialize the selection state (isSelecting) and capture both the start and end
     * positions at the same point.
     *
     * Finally, we collect all selectable storage item elements (shares and projects) from
     * the current view to be used in the selection calculations.
     */
    const wrapperRefCurrent = wrapperRef.current

    if (!wrapperRefCurrent) {
      return
    }

    const handlePointerDown = (event: PointerEvent) => {
      const { x: xWrapper, y: yWrapper } =
        wrapperRef.current!.getBoundingClientRect()

      setStartPosition({
        x: event.clientX - xWrapper,
        y: event.clientY - yWrapper,
        shift: event.shiftKey,
      })
      setEndPosition({
        x: event.clientX - xWrapper,
        y: event.clientY - yWrapper,
      })

      setIsSelecting(true)

      setSelectableElements(
        Array.from(
          document.querySelectorAll<HTMLElement>(
            '[data-share-id], [data-project-id]'
          )
        )
      )
    }

    wrapperRefCurrent.addEventListener('pointerdown', handlePointerDown)

    return () => {
      wrapperRefCurrent.removeEventListener('pointerdown', handlePointerDown)
    }
  }, [setShowCheckbox])

  /**
   * 1.1
   * Show selection checkboxes when the drag distance exceeds threshold
   * to prevent the initial flicker of checkboxes on click
   */
  useEffect(() => {
    if (
      Math.abs(startPosition.x - endPosition.x) < SELECTION_THRESHOLD ||
      Math.abs(startPosition.y - endPosition.y) < SELECTION_THRESHOLD ||
      !isSelecting
    ) {
      return
    }

    setShowCheckbox(true)
  }, [startPosition, endPosition, setShowCheckbox, isSelecting])

  /**
   * 1.2 Add/remove cursor style when shift key is pressed/released during selection
   */
  useEffect(() => {
    if (isSelecting && startPosition.shift) {
      document
        .getElementsByTagName('html')
        .item(0)
        ?.classList.add('cursor-copy')

      return () => {
        document
          .getElementsByTagName('html')
          .item(0)
          ?.classList.remove('cursor-copy')
      }
    }
  }, [startPosition.shift, isSelecting])

  const {
    elementsGroupedByHorizontalPosition,
    elementsGroupedByVerticalPosition,
    elementsTypeById,
    horizontalPositions,
    verticalPositions,
  } = useMemo(() => {
    /**
     * 2. Group element positions in vertical and horizontal records to optimize
     *    selection calculations. This runs when selectableElements state is updated on pointerdown.
     *
     * This useMemo creates groups of positions (both horizontal and vertical) and places
     * the individual elements in groups according to their position.
     *
     * A simplified example with a 3x3 grid:
     *
     *  [A][B][C]
     *  [D][E][F]
     *  [G][H][I]
     *
     * The vertical grouping (by X position) would be:
     *
     *  {
     *    1: [A, D, G],
     *    2: [B, E, H],
     *    3: [C, F, I],
     *  }
     *
     * and the horizontal grouping (by Y position) would be:
     *
     *  {
     *    1: [A, B, C],
     *    2: [D, E, F],
     *    3: [G, H, I],
     *  }
     *
     * This allows us to quickly determine which elements are within the selection area.
     * We save both left and left + width positions for vertical grouping, and top and
     * top + height positions for horizontal grouping to ensure we cover the entire area
     * of each element, since the selection area can start or end anywhere within an element.
     */

    const elementsGroupedByVerticalPosition: Record<string, string[]> = {}
    const elementsGroupedByHorizontalPosition: Record<string, string[]> = {}
    const elementsTypeById: Record<string, StorageItem['__typename']> = {}

    /**
     * "verticalPositionsSet" and "horizontalPositionsSet" are used to store unique
     * position pairs (start and end) for each axis. This helps in creating the final
     * arrays of positions used during selection calculations.
     */
    const verticalPositionsSet: Set<string> = new Set()
    const horizontalPositionsSet: Set<string> = new Set()

    if (selectableElements.length === 0) {
      return {
        elementsGroupedByVerticalPosition,
        elementsGroupedByHorizontalPosition,
        elementsTypeById,
        verticalPositions: [],
        horizontalPositions: [],
      }
    }

    const wrapperRect = wrapperRef.current!.getBoundingClientRect()

    selectableElements.forEach(element => {
      const { __typename, identifier } = normalizeElementToStorageItem(element)
      elementsTypeById[identifier] = __typename

      const elementRect = element.getBoundingClientRect()

      // Calculate element position relative to the wrapper
      const elementLeft = elementRect.left - wrapperRect.left
      const elementLeftPlusWidth = elementLeft + elementRect.width

      verticalPositionsSet.add(`${elementLeft}:${elementLeftPlusWidth}`)

      // Fetch the array of items at this left element position or initialize it
      const arrayOfItemsAtElementLeftEdgePosition =
        elementsGroupedByVerticalPosition[elementLeft] || []

      // Update the record with the new item
      arrayOfItemsAtElementLeftEdgePosition.push(identifier)
      elementsGroupedByVerticalPosition[elementLeft] =
        arrayOfItemsAtElementLeftEdgePosition

      // Fetch the array of items at this left + width element position or initialize it
      const arrayOfItemsAtElementRightEdgePosition =
        elementsGroupedByVerticalPosition[elementLeftPlusWidth] || []

      // Update the record with the new item
      arrayOfItemsAtElementRightEdgePosition.push(identifier)
      elementsGroupedByVerticalPosition[elementLeftPlusWidth] =
        arrayOfItemsAtElementRightEdgePosition

      const elementTop = elementRect.top - wrapperRect.top
      const elementTopPlusHeight = elementTop + elementRect.height

      horizontalPositionsSet.add(`${elementTop}:${elementTopPlusHeight}`)

      // Fetch the array of items at this top element position or initialize it
      const arrayOfItemsAtElementTopEdgePosition =
        elementsGroupedByHorizontalPosition[elementTop] || []

      // Update the record with the new item
      arrayOfItemsAtElementTopEdgePosition.push(identifier)
      elementsGroupedByHorizontalPosition[elementTop] =
        arrayOfItemsAtElementTopEdgePosition

      // Fetch the array of items at this top + height element position or initialize it
      const arrayOfItemsAtElementBottomEdgePosition =
        elementsGroupedByHorizontalPosition[elementTopPlusHeight] || []

      // Update the record with the new item
      arrayOfItemsAtElementBottomEdgePosition.push(identifier)
      elementsGroupedByHorizontalPosition[elementTopPlusHeight] =
        arrayOfItemsAtElementBottomEdgePosition
    })

    /**
     * "verticalPositions" and "horizontalPositions" arrays are created to facilitate iteration
     * over the positions during the selection calculations.
     */
    const verticalPositions = Array.from(verticalPositionsSet).map(item =>
      item.split(':').map(Number)
    )

    const horizontalPositions = Array.from(horizontalPositionsSet).map(item =>
      item.split(':').map(Number)
    )

    return {
      elementsGroupedByVerticalPosition,
      elementsGroupedByHorizontalPosition,
      verticalPositions,
      horizontalPositions,
      elementsTypeById,
    }
  }, [selectableElements])

  useEffect(() => {
    /**
     * 3. Update selection area on pointermove
     *
     * This will update the end position of the selection area as the user moves
     * the pointer while holding down the button.
     *
     * Note that we are attaching the event to the document to make sure we capture
     * the pointer movements even if it goes outside the wrapper area.
     */
    if (!isSelecting) {
      return
    }

    const handlePointerMove = (event: PointerEvent) => {
      const { x: xWrapper, y: yWrapper } =
        wrapperRef.current!.getBoundingClientRect()

      setEndPosition({
        x: event.clientX - xWrapper,
        y: event.clientY - yWrapper,
      })
    }

    document.addEventListener('pointermove', handlePointerMove)
    document.getElementById('root')?.classList.add('no-select')

    return () => {
      document.removeEventListener('pointermove', handlePointerMove)
      document.getElementById('root')?.classList.remove('no-select')
    }
  }, [isSelecting])

  useEffect(() => {
    /**
     * 4. Calculate selected items based on the selection area
     *
     * Using the grouped positions from step 2, we can efficiently determine which items are
     * within the selection area by comparing their positions on both the vertical and horizontal axes.
     *
     * Using the example from step 2:
     *
     *  [A][B][C]
     *  [D][E][F]
     *  [G][H][I]
     *
     * If the user selects from position (1, 1) to (2, 2):
     *
     * "elementsMatchedOnVerticalPosition" would contain A, B, C, D, E, F
     * "elementsMatchedOnHorizontalPosition" would contain A, D, G, B, E, H
     *
     * The intersection of both sets would be A, B, D, E which are the selected items.
     *
     * This works regardless of drag direction because we normalize to top-left and
     * bottom-right corners of the selection area.
     */
    if (!isSelecting) {
      return
    }

    const elementsMatchedOnVerticalPosition = new Set<string>()
    const elementsMatchedOnHorizontalPosition = new Set<string>()

    /**
     * Calculate the boundaries of the selection area regardless of the drag direction
     */
    const leftX = Math.min(startPosition.x, endPosition.x)
    const rightX = Math.max(startPosition.x, endPosition.x)
    const topY = Math.min(startPosition.y, endPosition.y)
    const bottomY = Math.max(startPosition.y, endPosition.y)

    /**
     * Iterate over the grouped positions to find which elements fall within the selection area
     */
    verticalPositions.forEach(verticalPosition => {
      const [leftVerticalPosition, rightVerticalPosition] = verticalPosition

      if (
        inRange(leftVerticalPosition, leftX, rightX) ||
        inRange(rightVerticalPosition, leftX, rightX) ||
        inRange(leftX, leftVerticalPosition, rightVerticalPosition) ||
        inRange(rightX, leftVerticalPosition, rightVerticalPosition)
      ) {
        elementsGroupedByVerticalPosition[leftVerticalPosition].forEach(id => {
          elementsMatchedOnVerticalPosition.add(id)
        })
      }
    })

    horizontalPositions.forEach(horizontalPosition => {
      const [topHorizontalPosition, bottomHorizontalPosition] =
        horizontalPosition

      if (
        inRange(topHorizontalPosition, topY, bottomY) ||
        inRange(bottomHorizontalPosition, topY, bottomY) ||
        inRange(topY, topHorizontalPosition, bottomHorizontalPosition) ||
        inRange(bottomY, topHorizontalPosition, bottomHorizontalPosition)
      ) {
        elementsGroupedByHorizontalPosition[topHorizontalPosition].forEach(
          id => {
            elementsMatchedOnHorizontalPosition.add(id)
          }
        )
      }
    })

    /**
     * Determine the final selected items by calculating the intersection of both sets
     */
    let elementsMatched = elementsMatchedOnVerticalPosition.intersection(
      elementsMatchedOnHorizontalPosition
    )

    if (startPosition.shift) {
      elementsMatched = previousSelectionSet.current.union(elementsMatched)
    }

    /**
     * Update the selection state with the new selected/deselected items
     */
    bulkItemSelection((itemsType, itemsIdSet) => {
      const newIdsSet = new Set(itemsIdSet)
      const newItemsType = { ...itemsType }

      const differencesInContext =
        elementsMatched.symmetricDifference(newIdsSet)

      differencesInContext.forEach(itemId => {
        if (elementsMatched.has(itemId)) {
          newIdsSet.add(itemId)
          newItemsType[itemId] = elementsTypeById[itemId]
        } else {
          newIdsSet.delete(itemId)
          delete newItemsType[itemId]
        }
      })

      return {
        itemsIdSet: newIdsSet,
        itemsType: newItemsType,
      }
    })
  }, [
    endPosition,
    startPosition,
    bulkItemSelection,
    isSelecting,
    verticalPositions,
    horizontalPositions,
    elementsGroupedByVerticalPosition,
    elementsGroupedByHorizontalPosition,
    elementsTypeById,
  ])

  useEffect(() => {
    /**
     * 5. End selection on pointerup
     *
     * This will reset the selection state and positions when the user releases
     * the pointer button.
     *
     * Note that we are attaching the event to the document to make sure we capture
     * the pointer release even if it happens outside the wrapper area.
     */
    const handlePointerUp = () => {
      if (!isSelecting) {
        return
      }

      /**
       * If the start and end positions are the same, it means the user just clicked
       * without dragging, so we clear any text selection that might have occurred.
       */
      if (
        startPosition.x === endPosition.x &&
        startPosition.y === endPosition.y
      ) {
        window.getSelection()?.removeAllRanges()
      }

      /**
       * Make sure to update the previous selection set to match the current selection in context
       */
      bulkItemSelection((itemsType, itemsIdSet) => {
        previousSelectionSet.current = itemsIdSet

        return {
          itemsIdSet,
          itemsType,
        }
      })

      // ⚠️ Order is important here
      //
      // We need to reset "isSelecting" first so the items stay selected otherwise
      // the selection items are recalculated because the start/end positions are reset
      setIsSelecting(false)

      setStartPosition(INITIAL_POSITION)
      setEndPosition(INITIAL_POSITION)
      setShowCheckbox(false)
    }

    document.addEventListener('pointerup', handlePointerUp)

    return () => {
      document.removeEventListener('pointerup', handlePointerUp)
    }
  }, [
    bulkItemSelection,
    endPosition,
    isSelecting,
    setShowCheckbox,
    startPosition,
  ])

  /**
   * Render the selection rectangle correctly regardless of drag direction by determining
   * the top-left corner based on the start and end positions.
   *
   * The rectangle dimensions are clamped to ensure it does not exceed the bounds of the wrapper.
   */

  const wrapperRect = wrapperRef.current?.getBoundingClientRect()

  const top = Math.min(startPosition.y, endPosition.y)
  const left = Math.min(startPosition.x, endPosition.x)
  const width = Math.min(
    Math.abs(startPosition.x - endPosition.x),
    (wrapperRect?.width || 0) - left
  )
  const height = Math.min(
    Math.abs(startPosition.y - endPosition.y),
    (wrapperRect?.height || 0) - top
  )

  return (
    <SelectionAreaWrapper
      data-testid="storage-items-selection-area-wrapper"
      ref={wrapperRef}
    >
      {isSelecting && (
        <SelectionAreaRect
          data-testid="storage-items-selection-area"
          style={{
            width,
            height,
            transform: `translate(${left}px, ${top}px)`,
          }}
        />
      )}
    </SelectionAreaWrapper>
  )
}
